import {
  Tabs,
  Tab,
  Box,
  TextField,
  styled,
  Grid,
  InputAdornment,
  TableRow,
  TableCell,
  Typography,
  Button,
  Autocomplete
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import React, { useEffect, useState, useRef } from 'react';
import PageHeader from '../../components/PageHeader';
import Table from '../../components/Table';
import { Link, useNavigate } from 'react-router-dom';
import { Company } from '../../schema/company';
import { PersonType } from '../../schema/person';
import { TableResponse } from '../../schema/helper';
import { api } from '../../api';
import dayjs from 'dayjs';
import CircularProgress from '@mui/material/CircularProgress';
import MultiSelect from '../../components/MultiSelect';
import { set } from 'date-fns';

const tableHead = [
  'เลขบัตรประชาชน',
  'ชื่อ นามสกุล',
  'บริษัท',
  'ประเภท',
  'ผู้คุ้มงาน',
  'วันที่เริ่ม',
  'วันที่สิ้นสุด',
  'Actions'
];

enum ControlType {
  All = 'all',
  In = 'in',
  Out = 'out'
}
interface ControlInOut {
  person_id: number;
  company: string;
  personnal_id: string;
  name: string;
  person_type: string;
  supervisor: string;
  temperature: string;
  covid_result: string;
  start_date: string;
  end_date: string;
  is_in: boolean;
  exchange_card_id?: string;
}
const ControlInOut = () => {
  const navigate = useNavigate();
  const [controlType, setControlType] = useState<ControlType>(ControlType.All);
  const [filterID, setFilterID] = useState<string>('');
  const [filterName, setFilterName] = useState<string>('');
  const [filterCrop, setFilterCrop] = useState<string>('');
  const [isSearchingID, setIsSearchingID] = useState<boolean>(false);

  const [filterCompanyIDList, setFilterCompanyIDList] = useState<number[]>([]);
  const [filterCompanyList, setFilterCompanyList] = useState<Company[]>([]);
  const [filterPersonTypeIDList, setFilterPersonTypeIDList] = useState<string[] | number[]>([]);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [companySearchKeyword, setCompanySearchKeyword] = useState<string>('');

  const [personTypes, setPersonTypes] = useState<PersonType[]>([]);
  const [tableData, setTableData] = useState<TableResponse<ControlInOut> | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const ref = useRef<string>();

  const goToInDetailPage = (person_id: number) => {
    navigate(`/control-in-out/in/${person_id}`);
  };
  const goToOutDetailPage = (person_id: number) => {
    navigate(`/control-in-out/out/${person_id}`);
  };

  const handleEnterToSearch = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key == 'Enter') {
      const searchParams = new URLSearchParams();
      searchParams.set('card_or_device_id', filterID);
      searchParams.set('offset', `0`);
      searchParams.set('limit', `10`);
      api
        .get<TableResponse<ControlInOut>>(`/control-in-out`, { params: searchParams })
        .then((res) => {
          const { data } = res;
          if (data.total == 1 && filterID) {
            if (data.data[0].is_in) {
              goToOutDetailPage(data.data[0].person_id);
              return;
            }
            goToInDetailPage(data.data[0].person_id);
          }
          setTableData(null);
        });
      setIsSearchingID(false);
    }
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    setIsLoading(true);
    const timeOutId = setTimeout(() => {
      ref.current = JSON.stringify({
        controlType,
        filterID,
        filterName,
        filterCompanyIDList,
        filterPersonTypeIDList,
        page,
        rowsPerPage
      });
      const is_in =
        controlType == ControlType.Out ? true : controlType == ControlType.In ? false : undefined;
      const searchParams = new URLSearchParams();

      if (filterID) {
        searchParams.set('card_or_device_id', filterID);
      }
      if (filterName) {
        searchParams.set('name', filterName);
      }
      if (filterCompanyIDList.length) {
        filterCompanyIDList.forEach((company_id) => {
          searchParams.append('company_id', `${company_id}`);
        });
      }
      if (filterPersonTypeIDList.length) {
        filterPersonTypeIDList.forEach((person_type_id) => {
          searchParams.append('person_type_id', `${person_type_id}`);
        });
      }
      if (is_in != undefined) {
        searchParams.set('is_in', `${is_in}`);
      }
      searchParams.set('offset', `${page * rowsPerPage}`);
      searchParams.set('limit', `${rowsPerPage}`);

      setIsSearchingID(false);
      api
        .get<TableResponse<ControlInOut>>(`/control-in-out`, { params: searchParams })
        .then((res) => {
          const { data } = res;
          console.log(data);

          if (
            data.total == 1 &&
            isSearchingID &&
            filterID != '' &&
            filterName == '' &&
            filterCompanyIDList.length == 0 &&
            filterPersonTypeIDList.length == 0
          ) {
            if (filterID == data.data[0].personnal_id || filterID == data.data[0].exchange_card_id) {
              if (data.data[0].is_in) {
                goToOutDetailPage(data.data[0].person_id);
                return;
              }
              goToInDetailPage(data.data[0].person_id);
            }
          }
          setTableData(data);
        })
        .finally(() => setIsLoading(false));
    }, 500);
    return () => clearTimeout(timeOutId);
  }, [
    controlType,
    filterID,
    filterName,
    filterCompanyIDList,
    filterPersonTypeIDList,
    page,
    rowsPerPage
  ]);

  useEffect(() => {
    api.get<PersonType[]>(`/person-type`).then((res) => {
      const { data } = res;
      setPersonTypes(data);
    });
  }, []);

  useEffect(() => {
    if (companySearchKeyword) {
      const timeOut = setTimeout(() => {
        const searchParams = new URLSearchParams();
        searchParams.set('company_name', companySearchKeyword);
        searchParams.set('limit', '25');

        api.get<Company[]>(`/company`, { params: searchParams }).then((res) => {
          const { data } = res;
          setCompanies(data);
        });
      }, 500);
      return () => clearTimeout(timeOut);
    } else {
      api
        .get<Company[]>(`/company`, {
          params: {
            limit: 25
          }
        })
        .then((res) => {
          const { data } = res;
          setCompanies(data);
        });
    }
  }, [companySearchKeyword]);

  // useEffect(() => {
  //   const filterSrt = sessionStorage.getItem('controlinout_filter');
  //   if (filterSrt) {
  //     const data: {
  //       controlType: ControlType;
  //       filterID: string;
  //       filterName: string;
  //       filterCompanyIDList: number[];
  //       filterPersonTypeIDList: number[];
  //       page: number;
  //       rowsPerPage: number;
  //     } = JSON.parse(filterSrt);
  //     setControlType(data.controlType);
  //     setFilterID(data.filterID);
  //     setFilterName(data.filterName);
  //     setFilterCompanyIDList(data.filterCompanyIDList);
  //     setFilterPersonTypeIDList(data.filterPersonTypeIDList);
  //     setPage(data.page);
  //     setRowsPerPage(data.rowsPerPage);
  //     if (data.filterCompanyIDList) {
  //       const searchParams = new URLSearchParams();
  //       searchParams.set('limit', `${data.filterCompanyIDList.length}`);
  //       data.filterCompanyIDList.forEach((company_id) => {
  //         searchParams.append('id', `${company_id}`);
  //       });
  //       api
  //         .get<Company[]>(`/company`, {
  //           params: searchParams
  //         })
  //         .then((res) => {
  //           const { data } = res;
  //           setFilterCompanyList(data);
  //         });
  //     }
  //   }
  //   return () => {
  //     if (ref.current) {
  //       sessionStorage.setItem('controlinout_filter', ref.current);
  //     }
  //   };
  // }, []);

  return (
    <Container>
      <PageHeader text="การควบคุมบุคคลเข้า-ออก" />
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={controlType}
          onChange={(_, value) => setControlType(value)}
          aria-label="basic tabs example"
        >
          <Tab label="ทั้งหมด" value={ControlType.All} />
          <Tab label="เข้า" value={ControlType.In} />
          <Tab label="ออก " value={ControlType.Out} />
        </Tabs>
      </Box>
      <Grid container spacing={2} marginTop={1}>
        <Grid item md={3}>
          <TextField
            id="search-text-label"
            label="ค้นหาเลขบัตรประชาชน / หมายเลขอุปกรณ์ติดตามตัว"
            variant="outlined"
            placeholder="ค้นหาเลขบัตรประชาชน / หมายเลขอุปกรณ์ติดตามตัว"
            onChange={(event) => {
              setFilterID(event.target.value);
              setIsSearchingID(true);
            }}
            fullWidth
            value={filterID}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <QrCode2Icon />
                </InputAdornment>
              )
            }}
            onKeyDown={handleEnterToSearch}
            autoComplete="off"
          />
        </Grid>
        <Grid item md={3}>
          <TextField
            id="search-text-label"
            label="ค้นหาชื่อ - นามสกุล"
            variant="outlined"
            placeholder="ค้นหาชื่อ - นามสกุล"
            value={filterName}
            onChange={(event) => setFilterName(event.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Grid>
        <Grid item md={3}>
          {/* <Autocomplete
            multiple
            limitTags={2}
            disablePortal
            options={personTypes}
            getOptionLabel={(option) => option.type_name}
            renderInput={(params) => <TextField {...params} label="ประเภทบุคคล" />}
            onChange={(_, value) => setFilterPersonTypeIDList(value.map((data) => data.id))}
            value={personTypes.filter((data) => data.id in filterPersonTypeIDList)}
          /> */}
          <MultiSelect
            options={personTypes.map((data) => ({ id: data.id.toString(), name: data.type_name }))}
            onChange={(data) => setFilterPersonTypeIDList(data)}
            label="ประเภทบุคคล"
          />
        </Grid>
        <Grid item md={3}>
          <Autocomplete
            multiple
            limitTags={2}
            disablePortal
            options={companies}
            getOptionLabel={(option) => (option?.company_name ? option.company_name : '')}
            renderInput={(params) => <TextField {...params} label="บริษัท" placeholder="บริษัท" />}
            onChange={(_, value) => {
              setCompanySearchKeyword('');
              setFilterCompanyIDList(value.map((data) => data.id));
              setFilterCompanyList(value);
            }}
            value={filterCompanyList}
            onInputChange={(_, newInputValue) => {
              setCompanySearchKeyword(newInputValue);
            }}
            isOptionEqualToValue={(option, v) => v?.id == option?.id}
            fullWidth
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        <Table
          heads={tableHead}
          count={tableData?.total ? tableData.total : 0}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          isLoading={isLoading}
        >
          {(!tableData || (tableData.total == 0 && filterID)) && (
            <>
              <Typography variant="h6" mt={3} ml={3} gutterBottom>
                ไม่พบข้อมูลในรายชื่อที่สามารถแลกบัตรได้
              </Typography>
              <Typography
                variant="subtitle2"
                mt={1}
                ml={3}
                gutterBottom
                component={Link}
                to={`/person?cid=${filterID}`}
              >
                กรุณาคลิกที่นี่เพื่อตรวจสอบหมายเลข `{filterID}` ในรายชื่อทั้งหมด
              </Typography>
            </>
          )}
          {tableData?.data.map((data, index) => (
            <TableRow key={index}>
              <TableCell align="center">{data.personnal_id}</TableCell>
              <TableCell align="center">{data.name}</TableCell>
              <TableCell align="center">{data.company}</TableCell>
              <TableCell align="center">{data.person_type}</TableCell>
              <TableCell align="center">{data.supervisor}</TableCell>
              <TableCell align="center">{dayjs(data.start_date).format('DD/MM/YYYY')}</TableCell>
              <TableCell align="center">{dayjs(data.end_date).format('DD/MM/YYYY')}</TableCell>
              <TableCell align="center">
                {data.is_in ? (
                  <Button
                    component={Link}
                    to={`/control-in-out/out/${data.person_id}`}
                    variant="contained"
                    color="error"
                  >
                    ออก
                  </Button>
                ) : (
                  <Button
                    component={Link}
                    to={`/control-in-out/in/${data.person_id}`}
                    variant="contained"
                    color="success"
                  >
                    เข้า
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </Table>
      </Box>
    </Container>
  );
};

export default ControlInOut;

const Container = styled('div')`
  padding: 16px;
`;
